if (!customElements.get('sooler-media-columns')) {
  customElements.define('sooler-media-columns', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.videoCards = Array.from(this.querySelectorAll('[data-sooler-media-columns-video]'));
      this.viewer = this.querySelector('[data-sooler-media-columns-viewer]');
      this.viewerFeed = this.querySelector('[data-sooler-media-columns-viewer-feed]');
      this.viewerClose = this.querySelector('[data-sooler-media-columns-viewer-close]');
      this.viewerPrevious = this.querySelector('[data-sooler-media-columns-viewer-prev]');
      this.viewerNext = this.querySelector('[data-sooler-media-columns-viewer-next]');
      this.viewerPlay = this.querySelector('[data-sooler-media-columns-viewer-play]');
      this.viewerSound = this.querySelector('[data-sooler-media-columns-viewer-sound]');
      this.activeVideoIndex = 0;
      this.viewerBuilt = false;
      this.viewerMuted = true;
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.initializeVideoCards();
      this.initializeViewer();
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.viewer && this.viewer.parentElement === document.body) this.appendChild(this.viewer);
      this.initialized = false;
    }

    initializeVideoCards() {
      this.videoCards.forEach((card, index) => {
        const video = card.querySelector('video');
        const openButton = card.querySelector('[data-sooler-media-columns-video-open]');
        const soundButton = card.querySelector('[data-sooler-media-columns-video-sound]');
        if (!video) return;

        const open = (event) => {
          event?.stopPropagation();
          this.openViewer(index);
        };

        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-sooler-media-columns-video-sound]')) return;
          open(event);
        });
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          open(event);
        });
        openButton?.addEventListener('click', open);
        soundButton?.addEventListener('click', (event) => {
          event.stopPropagation();
          video.muted = !video.muted;
          soundButton.classList.toggle('is-unmuted', !video.muted);
          soundButton.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
        });
      });
    }

    initializeViewer() {
      if (!this.viewer) return;
      this.viewerClose?.addEventListener('click', () => this.closeViewer());
      this.viewerPrevious?.addEventListener('click', () => this.activateViewerVideo(this.activeVideoIndex - 1));
      this.viewerNext?.addEventListener('click', () => this.activateViewerVideo(this.activeVideoIndex + 1));
      this.viewerPlay?.addEventListener('click', () => this.toggleViewerPlayback());
      this.viewerSound?.addEventListener('click', () => this.toggleViewerSound());
      this.viewer.addEventListener('click', (event) => {
        if (event.target === this.viewer) this.closeViewer();
      });
    }

    buildViewer() {
      if (this.viewerBuilt || !this.viewerFeed) return;
      this.videoCards.forEach((card, index) => {
        const sourceVideo = card.querySelector('video');
        if (!sourceVideo) return;
        const slide = document.createElement('div');
        const video = sourceVideo.cloneNode(true);
        slide.className = 'sooler-media-columns__viewer-slide';
        slide.dataset.viewerIndex = index;
        video.className = '';
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.controls = false;
        video.muted = true;
        video.preload = 'metadata';
        video.addEventListener('play', () => this.updateViewerControls());
        video.addEventListener('pause', () => this.updateViewerControls());
        slide.appendChild(video);
        slide.addEventListener('click', () => this.toggleViewerPlayback());
        this.viewerFeed.appendChild(slide);
      });
      this.viewerBuilt = true;
    }

    viewerSlides() {
      return Array.from(this.viewerFeed?.querySelectorAll('.sooler-media-columns__viewer-slide') || []);
    }

    activeViewerVideo() {
      return this.viewerSlides()[this.activeVideoIndex]?.querySelector('video') || null;
    }

    openViewer(index) {
      if (!this.viewer || !this.videoCards.length) return;
      this.buildViewer();
      this.lastFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.viewer.hidden = false;
      this.viewer.setAttribute('aria-hidden', 'false');
      document.body.appendChild(this.viewer);
      this.activateViewerVideo(index);
      this.viewerClose?.focus();
    }

    closeViewer() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewerSlides().forEach((slide) => slide.querySelector('video')?.pause());
      this.viewer.hidden = true;
      this.viewer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousBodyOverflow || '';
      this.appendChild(this.viewer);
      this.lastFocusedElement?.focus?.();
    }

    activateViewerVideo(index) {
      const slides = this.viewerSlides();
      if (!slides.length) return;
      this.activeVideoIndex = Math.max(0, Math.min(index, slides.length - 1));
      slides.forEach((slide, slideIndex) => {
        const video = slide.querySelector('video');
        const active = slideIndex === this.activeVideoIndex;
        slide.classList.toggle('is-active', active);
        if (!active && video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      const activeVideo = this.activeViewerVideo();
      if (activeVideo) {
        activeVideo.muted = this.viewerMuted;
        activeVideo.play()?.catch(() => this.updateViewerControls());
      }
      this.updateViewerControls();
    }

    toggleViewerPlayback() {
      const video = this.activeViewerVideo();
      if (!video) return;
      if (video.paused) video.play()?.catch(() => {});
      else video.pause();
      this.updateViewerControls();
    }

    toggleViewerSound() {
      const video = this.activeViewerVideo();
      if (!video) return;
      this.viewerMuted = !this.viewerMuted;
      video.muted = this.viewerMuted;
      this.updateViewerControls();
    }

    updateViewerControls() {
      const video = this.activeViewerVideo();
      if (!video) return;
      if (this.viewerPrevious) this.viewerPrevious.disabled = this.activeVideoIndex === 0;
      if (this.viewerNext) this.viewerNext.disabled = this.activeVideoIndex === this.viewerSlides().length - 1;
      this.viewerPlay?.classList.toggle('is-paused', video.paused);
      this.viewerPlay?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      this.viewerSound?.classList.toggle('is-unmuted', !this.viewerMuted);
      this.viewerSound?.setAttribute('aria-label', this.viewerMuted ? 'Turn sound on' : 'Turn sound off');
    }

    handleKeydown(event) {
      if (!this.viewer || this.viewer.hidden) return;
      if (event.key === 'Escape') this.closeViewer();
      if (event.key === 'ArrowLeft') this.activateViewerVideo(this.activeVideoIndex - 1);
      if (event.key === 'ArrowRight') this.activateViewerVideo(this.activeVideoIndex + 1);
    }

    handleBlockSelect(event) {
      const card = event.target.closest?.('[data-sooler-media-columns-card]');
      if (!card || !this.contains(card)) return;
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  });
}
